/**
 * Comparable lot/batch identifier. Blank values are ignored.
 * Spaces and letter case do not make two identifiers different.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeBatchIdentifier(value) {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
}

/**
 * True when this order line is not being received.
 *
 * @param {Object|null|undefined} item
 * @returns {boolean}
 */
function isDidNotArrive(item) {
  return (
    item?.status === 'Did not arrive' || item?.selectedReason === 'Did not arrive'
  );
}

/**
 * First-seen display labels for batch numbers that appear more than once.
 * Empty batch rows are not identifiers.
 *
 * @param {Array<{ batch?: string }>|null|undefined} lots
 * @returns {string[]}
 */
export function duplicateBatchLabels(lots) {
  if (!Array.isArray(lots)) return [];
  const firstSeen = new Map();
  const counts = new Map();
  lots.forEach(lot => {
    const raw = lot?.batch == null ? '' : String(lot.batch).trim();
    const key = normalizeBatchIdentifier(raw);
    if (!key) return;
    if (!firstSeen.has(key)) firstSeen.set(key, raw);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const labels = [];
  counts.forEach((count, key) => {
    if (count > 1) labels.push(firstSeen.get(key));
  });
  return labels;
}

/**
 * True when this lot's batch number is shared with another lot in the same list.
 *
 * @param {Array<{ batch?: string }>|null|undefined} lots
 * @param {{ batch?: string }|null|undefined} lot
 * @returns {boolean}
 */
export function lotBatchIsDuplicate(lots, lot) {
  const key = normalizeBatchIdentifier(lot?.batch);
  if (!key) return false;
  return duplicateBatchLabels(lots).some(
    label => normalizeBatchIdentifier(label) === key
  );
}

/**
 * Batch numbers repeated on one catalogue line, and on each kit component.
 *
 * @param {Object|null|undefined} item
 * @returns {{ line: string[], kitComponents: string[][] }}
 */
export function itemDuplicateBatchGroups(item) {
  if (!item || isDidNotArrive(item)) {
    return { line: [], kitComponents: [] };
  }
  const kitComponents = Array.isArray(item.kitComponentLots)
    ? item.kitComponentLots.map(group => duplicateBatchLabels(group?.lots))
    : [];
  return {
    line: duplicateBatchLabels(item.lotItems),
    kitComponents,
  };
}

/**
 * True when this line, or one of its kit components, repeats a batch number.
 *
 * @param {Object|null|undefined} item
 * @returns {boolean}
 */
export function hasItemDuplicateBatches(item) {
  const groups = itemDuplicateBatchGroups(item);
  return (
    groups.line.length > 0 ||
    groups.kitComponents.some(labels => labels.length > 0)
  );
}

/**
 * True when any received line repeats a batch number.
 *
 * @param {unknown} items
 * @returns {boolean}
 */
export function hasAnyDuplicateBatches(items) {
  if (!Array.isArray(items) || items.length === 0) return false;
  return items.some(hasItemDuplicateBatches);
}

/**
 * Callout copy for one set of repeated batch numbers.
 *
 * @param {string[]} labels
 * @param {'line'|'kit component'} [scope]
 * @returns {string}
 */
export function formatDuplicateBatchError(labels, scope = 'line') {
  if (!Array.isArray(labels) || labels.length === 0) return '';
  const where =
    scope === 'kit component' ? 'on this kit component' : 'on this line';
  if (labels.length === 1) {
    return `Batch ${labels[0]} is entered more than once ${where}. Each batch needs its own batch number.`;
  }
  const list =
    labels.length === 2
      ? `${labels[0]} and ${labels[1]}`
      : `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
  return `Batches ${list} are each entered more than once ${where}. Each batch needs its own batch number.`;
}
